import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { formatDistanceStrict } from 'date-fns';
import React from 'react';

import { ConversionRatesInfo } from '@/components/molecules/ConversionRatesInfo';
import { TransactionDetailsEntry } from '@/components/molecules/TransactionDetailsEntry';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { useTransactionDetails } from '@/hooks/useTransactionDetails';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { Amount } from '@/types/amount';
import { CurrencyCodeType } from '@/types/currency';
import { Locale } from '@/types/locale';
import { NfTransactionDetailsResponse } from '@/types/simulation';
import { FeeType, FixedFee, PercentageFee } from '@/types/transaction';
import { formatAmount, FormatStyle } from '@/utility/formatAmount';
import { toDateFnsLocale } from '@/utility/toDateFnsLocale';

interface Props {
  readonly transactionDetails: NfTransactionDetailsResponse;
  readonly fromAmount: number | null;
  readonly fromCurrency: CurrencyCodeType;
  readonly toAmount: number | null;
  readonly toCurrency: CurrencyCodeType;
  readonly textVariant?: 'body2' | 'body1';
  readonly validityTimestamp?: string;
  readonly fxLockSpreadPercentage?: number | null;
  readonly payorName?: string;
}

interface DetailsField {
  readonly label: string | React.ReactElement;
  readonly value: string | React.ReactElement | null;
  readonly help?: string;
}

const PERCENT_FORMATTER_CONFIG = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  style: 'percent' as const,
};

export const TransactionDetails: React.FC<Props> = (props: Props): React.ReactElement => {
  const {
    transactionDetails,
    fromAmount,
    fromCurrency,
    toAmount,
    toCurrency,
    textVariant = 'body1',
    validityTimestamp = null,
    fxLockSpreadPercentage = null,
    payorName,
  } = props;
  const details = useTransactionDetails(transactionDetails, fromAmount, fromCurrency);
  const taxes = transactionDetails.feeDetails.percentageFees.filter(
    (fee: PercentageFee): boolean => fee.type === FeeType.TAX,
  );

  const toAmountObject = React.useMemo(
    (): Amount => ({
      value: toAmount,
      currencyCode: toCurrency,
    }),
    [toAmount, toCurrency],
  );

  const tr = useTranslator();
  const locale = React.useContext<Locale>(LocaleContext);
  const currencyFormatter = useCurrencyFormatter();
  const percentFormatter = useLocalizedFormatter(PERCENT_FORMATTER_CONFIG);
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();

  const fields = React.useMemo(
    (): readonly DetailsField[] => [
      ...(payorName ? [{ label: tr('PAYOR'), value: payorName }] : []),
      { label: tr('GROSS_AMOUNT'), value: details.grossAmountToConvert },
      ...(details.percentageFeesWithoutTaxes.length > 0
        ? details.percentageFeesWithoutTaxes.map(
            (fee: PercentageFee): DetailsField => ({
              label: tr('TRANSFER_FEES_WITH_PERCENTAGE', fee.percentageRate),
              value: fee.calculatedFee,
            }),
          )
        : [
            {
              label: tr('TRANSFER_FEES_FREE'),
              value: formatAmount(
                locale,
                0,
                fromCurrency,
                FormatStyle.extended,
                2,
                displayUsdcAsUsd,
              ),
            },
          ]),
      ...(details.fixedFees.length > 0
        ? details.fixedFees.map(
            (fee: FixedFee): DetailsField => ({
              label: tr('PAYMENT_RAIL_FEE', fee.paymentRail),
              value: fee.value,
            }),
          )
        : []),
      ...(taxes.length > 0
        ? taxes.map(
            (tax: PercentageFee): DetailsField =>
              tax.feeCurrency === CurrencyCodeType.BRL
                ? {
                    label: tr(
                      'IOF_WITH_PERCENTAGE',
                      `${percentFormatter.format(Number(tax.percentageRate) / 100)}`,
                    ),
                    value: formatAmount(
                      locale,
                      Number(tax.calculatedFee),
                      tax.feeCurrency,
                      FormatStyle.extended,
                      2,
                      displayUsdcAsUsd,
                    ),
                  }
                : {
                    label: tr(
                      'TAX',
                      `${percentFormatter.format(Number(tax.percentageRate) / 100)}`,
                    ),
                    value: formatAmount(
                      locale,
                      Number(tax.calculatedFee),
                      tax.feeCurrency,
                      FormatStyle.extended,
                      2,
                      displayUsdcAsUsd,
                    ),
                  },
          )
        : []),
    ],
    [
      tr,
      payorName,
      details.grossAmountToConvert,
      details.percentageFeesWithoutTaxes,
      details.fixedFees,
      fromCurrency,
      taxes,
      percentFormatter,
      locale,
      displayUsdcAsUsd,
    ],
  );

  const summary = React.useMemo(
    (): readonly DetailsField[] =>
      [
        {
          label: (
            <Typography variant={textVariant} color="primary">
              {tr('TOTAL_AMOUNT')}
            </Typography>
          ),
          value: toAmount ? (
            <Typography variant={textVariant} color="primary">
              {currencyFormatter.format(toAmountObject.value, toAmountObject.currencyCode)}
            </Typography>
          ) : (
            <Typography variant={textVariant}>{currencyFormatter.format(0, toCurrency)}</Typography>
          ),
        },
      ].filter(
        (item: DetailsField | null): item is DetailsField => item?.value !== null,
      ) as readonly DetailsField[],
    [
      currencyFormatter,
      textVariant,
      toAmount,
      toAmountObject.currencyCode,
      toAmountObject.value,
      toCurrency,
      tr,
    ],
  );

  const validityFormatted = React.useMemo((): string | null => {
    if (!validityTimestamp) {
      return null;
    }

    const validityDate = new Date(validityTimestamp);

    if (isNaN(validityDate.getTime())) {
      console.error('Invalid validityTimestamp provided:', validityTimestamp);
      return null;
    }

    const now = new Date();

    return formatDistanceStrict(validityDate, now, {
      locale: toDateFnsLocale(locale),
    });
  }, [locale, validityTimestamp]);

  return (
    <Box display="flex" flexDirection="column" gap={3} minWidth={344}>
      <Box display="flex" flexDirection="column" gap={1}>
        {fields.map(
          (item: any): React.ReactElement => (
            <TransactionDetailsEntry
              key={item.label /* This is the perfect key, duplication is not acceptable */}
              textVariant={textVariant}
              label={item.label}
              value={item.value}
              help={item.help}
            />
          ),
        )}
        {summary.map(
          (item: any): React.ReactElement => (
            <TransactionDetailsEntry
              key={item.label /* This is the perfect key, duplication is not acceptable */}
              textVariant={textVariant}
              label={item.label}
              value={item.value}
              help={item.help}
            />
          ),
        )}
      </Box>

      <ConversionRatesInfo
        marketExchangeRate={details.marketExchangeRate}
        conversionRate={details.conversionRate}
        integratorRate={details.integratorRate}
        integratorSpreadFeePercentage={details.integratorSpreadFeePercentage}
        effectiveTransactionValue={details.effectiveTransactionValue}
        fromCurrency={fromCurrency}
        toCurrency={toCurrency}
        calizaExchangeSpreadFeePercentage={details.calizaExchangeSpreadFeePercentage}
        fxLockSpreadPercentage={fxLockSpreadPercentage}
      />

      {validityFormatted && (
        <Box pb="6px" textAlign="center">
          <Typography variant="body1" color="custom.LightGray">
            <Typography component="span">{tr('THIS_QUOTE_WILL_REMAIN_ACTIVE_FOR')} </Typography>
            <Typography component="span" color="text.primary">
              {validityFormatted}
            </Typography>
          </Typography>
        </Box>
      )}
    </Box>
  );
};
